import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

export interface StoredBiometricSession {
  refreshToken: string;
  idToken: string;
  expiresAt: number; // Biometric session expiry (timestamp)
  createdAt: number;
}

const BIOMETRIC_ENABLED_KEY = 'biometric_enabled';
const HAS_BEEN_ASKED_KEY = 'biometric_has_been_asked';

// Storage abstraction using Capacitor Preferences for native secure storage
// Biometrics are MOBILE-ONLY so we use native storage on mobile and no-op on web
const storage = {
  async set(key: string, value: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    await Preferences.set({ key, value });
  },

  async get(key: string): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) return null; // Web returns null - biometrics not available

    const { value } = await Preferences.get({ key });
    return value;
  },

  async remove(key: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    await Preferences.remove({ key });
  }
};

/**
 * Set biometric authentication preference
 */
export const setBiometricEnabled = async (enabled: boolean): Promise<void> => {
  try {
    await storage.set(BIOMETRIC_ENABLED_KEY, enabled.toString());
  } catch (error) {
    console.error('[BiometricStorage] Failed to set preference:', error);
    throw error;
  }
};

/**
 * Get biometric authentication preference
 */
export const isBiometricEnabled = async (): Promise<boolean> => {
  try {
    const result = await storage.get(BIOMETRIC_ENABLED_KEY);
    return result === 'true';
  } catch (error) {
    console.error('[BiometricStorage] Failed to get preference:', error);
    return false;
  }
};

/**
 * Set whether user has been asked about biometric authentication
 */
export const setHasBeenAskedForBiometrics = async (asked: boolean): Promise<void> => {
  try {
    await storage.set(HAS_BEEN_ASKED_KEY, asked.toString());
  } catch (error) {
    console.error('[BiometricStorage] Failed to set has been asked:', error);
  }
};

/**
 * Get whether user has been asked about biometric authentication
 */
export const getHasBeenAskedForBiometrics = async (): Promise<boolean> => {
  try {
    const result = await storage.get(HAS_BEEN_ASKED_KEY);
    return result === 'true';
  } catch (error) {
    console.error('[BiometricStorage] Failed to get has been asked:', error);
    return false;
  }
};

/**
 * Clear all biometric-related data
 */
export const clearBiometricData = async (): Promise<void> => {
  await Promise.all([setBiometricEnabled(false), setHasBeenAskedForBiometrics(false)]);
};
